import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Badge from './Badge';
import LoadingSpinner from './LoadingSpinner';
import {
  PlusIcon,
  CheckIcon,
  TrashIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const WipFormModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  technicians,
  handleDelete,
  isEditing,
  disabled,
  loading
}) => {
  const [jobSiteQuery, setJobSiteQuery] = useState('');
  const [jobSiteResults, setJobSiteResults] = useState([]);
  const [isJobSiteDropdownVisible, setIsJobSiteDropdownVisible] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const [showLinkEditor, setShowLinkEditor] = useState(false);
  const [links, setLinks] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);

  // default notes modal state
  const [showDefaultNotesModal, setShowDefaultNotesModal] = useState(false);
  const [contractSearch, setContractSearch] = useState('');
  const [contractResults, setContractResults] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractDebounceTimeout, setContractDebounceTimeout] = useState(null);

  const inspectionId = formData?.inspection_id || formData?.id;

  useEffect(() => {
    const fetchLinks = async () => {
      if (!isOpen || !inspectionId) return;
      try {
        const { data } = await axios.get(`${apiUrl}/wip_crud.php`, {
          params: { getLinks: 1, inspection_id: inspectionId },
        });
        const ok = Array.isArray(data) && data[0] === 'success';
        setLinks(ok ? (data[1] || []) : []);
      } catch {
        setLinks([]);
      }
    };

    fetchLinks();
  }, [isOpen, inspectionId, apiUrl]);

  useEffect(() => {
    if (!isOpen) {
      setShowLinkEditor(false);
      setPendingRows([]);
      setShowDefaultNotesModal(false);
      setContractSearch('');
      setContractResults([]);
      setContractLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setJobSiteQuery('');
      setJobSiteResults([]);
      setIsJobSiteDropdownVisible(false);
    }
  }, [isOpen]);

 const handleMainModalClose = () => {
  if (showDefaultNotesModal) return;
  onClose();
  }; 

  const addPendingRow = () => {
    setShowLinkEditor(true);
    setPendingRows(prev => [...prev, { label: '', link: '' }]);
  };

  const updatePendingRow = (idx, field, value) => {
    setPendingRows(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const savePendingRow = async (idx) => {
    if (!inspectionId) {
      toast.error('Missing inspection id', { position: 'top-center' });
      return;
    }

    const row = pendingRows[idx];
    if (!row.label?.trim() || !row.link?.trim()) {
      toast.error('Title and Link are required.', { position: 'top-center' });
      return;
    }

    try {
      const payload = {
        status: 'addLink',
        data: {
          link_id: null,
          inspection_id: inspectionId,
          label: row.label.trim(),
          link: row.link.trim()
        }
      };

      const { data } = await axios.post(`${apiUrl}/wip_crud.php`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;

      if (ok) {
        const newId = Array.isArray(data) ? (data[2]?.id ?? data[1]?.id) : data?.id;
        setLinks(prev => [
          ...prev,
          {
            id: newId,
            inspection_id: inspectionId,
            label: row.label.trim(),
            link: row.link.trim()
          }
        ]);
        setPendingRows(prev => prev.filter((_, i) => i !== idx));
        toast.success('Link saved', { position: 'top-center' });
      } else {
        toast.error(
          (Array.isArray(data) && data[1]) || data?.error || 'Failed to save link',
          { position: 'top-center' }
        );
      }
    } catch (e) {
      toast.error('Error saving link', { position: 'top-center' });
    }
  };

  const deleteSavedLink = async (id) => {
    try {
      const payload = { status: 'deleteLink', data: { id } };
      const { data } = await axios.post(`${apiUrl}/wip_crud.php`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const ok = (Array.isArray(data) && data[0] === 'success') || data?.success;
      if (ok) {
        setLinks(prev => prev.filter(l => l.id !== id));
        toast.success('Link deleted', { position: 'top-center' });
      } else {
        toast.error(
          (Array.isArray(data) && data[1]) || data?.error || 'Failed to delete link',
          { position: 'top-center' }
        );
      }
    } catch {
      toast.error('Error deleting link', { position: 'top-center' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleJobSiteInputChange = (e) => {
    const value = e.target.value;
    setJobSiteQuery(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (value.length >= 3) {
      const timeout = setTimeout(async () => {
        try {
          const response = await axios.get(`${apiUrl}/wip_crud.php`, {
            params: { getSchedulerJobSites: value },
          });
          const results = Array.isArray(response.data) ? response.data : [];
          setJobSiteResults(results);
          setIsJobSiteDropdownVisible(true);
        } catch (error) {
          console.error('Error fetching job sites:', error);
        }
      }, 500);

      setDebounceTimeout(timeout);
    } else {
      setJobSiteResults([]);
      setIsJobSiteDropdownVisible(false);
    }
  };

  const handleJobSiteSelect = (jobSite) => {
    setJobSiteQuery(jobSite.job_site);
    setIsJobSiteDropdownVisible(false);

    setFormData((prevData) => ({
      ...prevData,
      job_site: jobSite.job_site,
      job_number: jobSite.job_number
    }));
  };

  const openDefaultNotesModal = () => {
    const seedAddress = formData?.job_site || '';
    setContractSearch(seedAddress);
    setContractResults([]);
    setShowDefaultNotesModal(true);

    if (seedAddress.trim().length >= 3) {
      searchMaintenanceContracts(seedAddress);
    }
  };

  const searchMaintenanceContracts = async (searchValue) => {
    try {
      setContractLoading(true);

      const response = await axios.get(`${apiUrl}/wip_crud.php`, {
        params: {
          getMaintenanceContractsByAddress: searchValue
        }
      });
      setContractResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error searching maintenance contracts:', error);
      toast.error('Failed to search contracts', { position: 'top-center' });
      setContractResults([]);
    } finally {
      setContractLoading(false);
    }
  };

  const handleContractSearchChange = (e) => {
    const value = e.target.value;
    setContractSearch(value);

    if (contractDebounceTimeout) {
      clearTimeout(contractDebounceTimeout);
    }

    if (value.trim().length >= 3) {
      const timeout = setTimeout(() => {
        searchMaintenanceContracts(value);
      }, 500);

      setContractDebounceTimeout(timeout);
    } else {
      setContractResults([]);
    }
  };

  const buildDefaultNoteText = (contract) => {
    const parts = [];

    if (contract.contract_type) parts.push(`Contract Type: ${contract.contract_type}`);
    if (contract.contract_number) parts.push(`Contract #: ${contract.contract_number}`);
    if (contract.system_type) parts.push(`System Type: ${contract.system_type}`);
    if (contract.inspection_frequency) parts.push(`Inspection Frequency: ${contract.inspection_frequency}`);
    if (contract.notes) parts.push(`Contract Notes: ${contract.notes}`);

    return parts.join('\n');
  };

  const handleUseDefaultNote = (contract) => {
    const defaultNote = buildDefaultNoteText(contract);

    setFormData((prev) => ({
      ...prev,
      user_comment: prev.user_comment?.trim()
        ? `${prev.user_comment}\n\n${defaultNote}`
        : defaultNote
    }));

    setShowDefaultNotesModal(false);
    toast.success('Default notes added', { position: 'top-center' });
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
            as="div"
            className="relative z-20"
            onClose={handleMainModalClose}
          >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {/* <div className="fixed inset-0 bg-black bg-opacity-30" /> */}
            <div
              className={`fixed inset-0 bg-black bg-opacity-30 ${
                showDefaultNotesModal ? 'pointer-events-none' : ''
              }`}
            />            
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/30 backdrop-blur-sm">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95 translate-y-4"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-4"
              >
                <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
                  <div className="flex items-center justify-between border-b border-gray-200 bg-purple-400 px-6 py-4">
                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                      {formData.id ? 'Edit WIP Schedule' : 'New WIP Schedule'}
                    </Dialog.Title>

                    {formData.tentative ? (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-600/20">
                        Tentative
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-600/20">
                        Active
                      </span>
                    )}
                  </div>

                  <form id="wipForm" onSubmit={onSubmit} className="px-6 py-5 space-y-6">
                    <div>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="tentative"
                          checked={!!formData.tentative}
                          onChange={handleInputChange}
                          className="h-4 w-4 rounded border-red-400 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm font-semibold text-red-600">Tentative</span>
                      </label>
                    </div>

                    {formData.tentative ? (
                      <div>
                        <label className="block text-sm font-medium text-red-700">
                          Status Description
                        </label>
                        <textarea
                          name="status_description"
                          value={formData.status_description || ''}
                          onChange={handleInputChange}
                          rows={3}
                          className="mt-1 block w-full rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-gray-900 focus:border-red-500 focus:ring-red-500"
                          placeholder="Explain why this job is tentative..."
                          required
                        />
                      </div>
                    ) : null}

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Job Number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="job_site"
                          value={jobSiteQuery ? jobSiteQuery : formData.schedule_id ? formData.job_site : ''}
                          onChange={handleJobSiteInputChange}
                          className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />

                        {isJobSiteDropdownVisible && jobSiteResults.length > 0 && (
                          <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto divide-y-2">
                            {jobSiteResults.map((jobSite) => {
                              const liBgClass = jobSite.status === 0
                                ? 'bg-red-100 hover:bg-red-200'
                                : 'hover:bg-gray-100';

                              return (
                                <li
                                  key={jobSite.job_number}
                                  className={`${liBgClass} px-4 py-2 cursor-pointer`}
                                  onClick={() => handleJobSiteSelect(jobSite)}
                                >
                                  {jobSite.project_hold_amount && (
                                    <div className='text-red-800 p-1 text-xs'>********** Project On Hold **********</div>
                                  )}
                                  <div className="text-sm font-medium text-blue-900">
                                    {jobSite.hold === 1 && (
                                      <Badge color="pink" feed="On Hold" />
                                    )}
                                    <span className="bg-yellow-50 p-1">
                                      {jobSite.customer_name}
                                    </span>{' '}
                                    | {jobSite.f_p_type} | {jobSite.type}
                                  </div>
                                  <div className="font-medium">
                                    {jobSite.job_site}
                                  </div>
                                  <div className="text-sm text-green-700">
                                    Job#: {jobSite.job_number}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Schedule Date
                        </label>
                        <input
                          type="date"
                          name="schedule_from"
                          value={formData.schedule_from}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Time In
                        </label>
                        <input
                          type="time"
                          name="start_time"
                          value={formData.start_time}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          End Date
                        </label>
                        <input
                          type="date"
                          name="schedule_to"
                          value={formData.schedule_to || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Time Out
                        </label>
                        <input
                          type="time"
                          name="end_time"
                          value={formData.end_time}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Lead Technician
                        </label>
                        <select
                          name="tech_id"
                          value={formData.tech_id || ''}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                          required
                        >
                          <option value="">Select Technician</option>
                          {technicians.map((tech) => (
                            <option key={tech.id} value={tech.id}>
                              {tech.first_name} {tech.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Additional Technician
                        </label>
                        <select
                          name="additional_tech_id"
                          value={formData.additional_tech_id}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          <option value="">Additional Tech</option>
                          {technicians.map((tech) => (
                            <option key={tech.id} value={tech.id}>
                              {tech.first_name} {tech.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          Instructions / Notes
                        </label>

                        <button
                          type="button"
                          onClick={openDefaultNotesModal}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                        >
                          + Add Default Notes
                        </button>
                      </div>

                      <textarea
                        name="user_comment"
                        value={formData.user_comment || ''}
                        onChange={handleInputChange}
                        rows={4}
                        className="mt-1 block w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>

                    {showLinkEditor && (
                      <div className="rounded-xl border border-gray-200 bg-white/70 p-4">
                        <div className="mb-2 flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <LinkIcon className="h-4 w-4" /> Links
                          </h4>
                          <button
                            type="button"
                            onClick={addPendingRow}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                          >
                            <PlusIcon className="h-4 w-4" /> Add
                          </button>
                        </div>

                        {links.length > 0 && (
                          <ul className="mb-3 space-y-2">
                            {links.map((l) => (
                              <li key={l.id} className="flex items-center gap-2 rounded-lg bg-gray-50 p-2">
                                <span className="truncate text-sm font-medium text-gray-900">{l.label}</span>
                                <span className="mx-1 text-gray-300">•</span>
                                <a
                                  href={l.link.startsWith('http') ? l.link : `https://${l.link}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="truncate text-sm text-indigo-600 hover:underline"
                                >
                                  {l.link}
                                </a>
                                <button
                                  type="button"
                                  onClick={() => deleteSavedLink(l.id)}
                                  className="ml-auto inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-600/20 hover:bg-red-100"
                                  title="Delete link"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        {pendingRows.length > 0 && (
                          <div className="space-y-2">
                            {pendingRows.map((row, idx) => (
                              <div key={idx} className="grid grid-cols-12 gap-2">
                                <input
                                  type="text"
                                  placeholder="Title"
                                  value={row.label}
                                  onChange={(e) => updatePendingRow(idx, 'label', e.target.value)}
                                  className="col-span-4 rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                />
                                <input
                                  type="url"
                                  placeholder="https://link"
                                  value={row.link}
                                  onChange={(e) => updatePendingRow(idx, 'link', e.target.value)}
                                  className="col-span-6 rounded-lg border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                />
                                <div className="col-span-2 flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => savePendingRow(idx)}
                                    className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-2.5 py-2 text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    title="Save"
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={addPendingRow}
                                    className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-2.5 py-2 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    title="Add another"
                                  >
                                    <PlusIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {links.length === 0 && pendingRows.length === 0 && (
                          <p className="text-xs text-gray-500">No links yet. Click <strong>Add</strong> to create one.</p>
                        )}
                      </div>
                    )}
                  </form>

                  <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                    {isEditing && (
                      <>
                        <button
                          type="button"
                          onClick={handleDelete}
                          className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          Delete
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setShowLinkEditor(v => !v);
                            if (pendingRows.length === 0 && links.length === 0) addPendingRow();
                          }}
                          className="inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          {showLinkEditor ? 'Hide Links' : 'Show Links'}
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      form="wipForm"
                      className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {formData.id ? 'Update' : 'Create'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Default Notes Modal */}
      <Transition appear show={showDefaultNotesModal} as={Fragment}>
        <Dialog as="div" className="relative z-30" onClose={() => setShowDefaultNotesModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white shadow-xl ring-1 ring-gray-200">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Add Default Notes
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Search maintenance contracts by address and add contract notes to the instruction field.
                    </p>
                  </div>

                  <div className="px-6 py-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Address Search
                      </label>
                      <div className="relative mt-1">
                        <MagnifyingGlassIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          value={contractSearch}
                          onChange={handleContractSearchChange}
                          placeholder="Type at least 3 characters..."
                          className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    {contractLoading ? (
                      <LoadingSpinner children="Searching contracts..." />
                    ) : contractResults.length > 0 ? (
                      <div className="max-h-96 overflow-auto rounded-xl border border-gray-200 divide-y">
                        {contractResults.map((contract) => (
                          <div
                            key={`${contract.source_table}-${contract.id}`}
                            className="p-4 hover:bg-gray-50"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                  <DocumentTextIcon className="h-4 w-4 text-indigo-500" /> 
                                  <span>
                                  <Badge
                                    color={contract.source_table === 'Maintenance' ? 'green' : 'red'}
                                    feed={contract.source_table === 'Maintenance' ? 'Contract' : 'Job Site'}/>
                                  </span> 
                                  {contract.contract_address || contract.address}
                                </div>
                           

                                <div className="mt-1 text-sm text-gray-600">
                                  {contract.job_site_address && (
                                    <div>
                                      {contract.job_site_address}
                                      { contract.job_site_city && contract.job_site_state && (
                                        <span>, {contract.job_site_city}, {contract.job_site_state}</span>
                                      )}
                                    </div>
                                  )}
                                  {contract.contract_type && <div><span className="font-semibold text-gray-700">Type:</span> {contract.contract_type}</div>}
                                  {contract.system_type && 
                                  <div><span className="font-semibold text-gray-700">System:
                                      </span> {contract.system_type}</div>}
                                  
                                  {contract.frequency && 
                                  <div><span className="font-semibold text-gray-700">Frequency:</span>
                                  {
                                  contract.frequency === 12
                                      ? '(A)'
                                      : contract.frequency === 3
                                      ? '(Q)'
                                      : contract.frequency === 1
                                      ? '(M)'
                                      : contract.frequency === 6
                                      ? '(SA)'
                                      : ''}                                    
                                  </div>}
                                  {/* {contract.frequency && <div>Frequency: {contract.frequency}</div>} */}
                                  {contract.notes && (
                                    <div className="mt-2 rounded-md bg-gray-50 p-2 text-xs text-gray-700 whitespace-pre-wrap">
                                      {contract.notes}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleUseDefaultNote(contract)}
                                className="shrink-0 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                              >
                                Use Notes
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                        No contracts found.
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-6 py-4">
                    <button
                      type="button"
                      onClick={() => setShowDefaultNotesModal(false)}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default WipFormModal;